import {
  CurrentSubscriptionStatus,
  ResumeSubscriptionMutation,
  ResumeSubscriptionMutationVariables,
  SubscriptionCreditBalanceQuery
} from "@outschool/gql-frontend-generated";
import { gql, useMutation } from "@outschool/ui-apollo";
import { useCallback, useState } from "react";

import { sleep } from "../../../../shared/helpers";

export const resumeSubscriptionMutation = gql`
  mutation ResumeSubscription {
    resumeSubscription
  }
`;

interface UseResumeSubscriptionOptions {
  refetchSubscriptionCreditBalance: () => Promise<{
    data: SubscriptionCreditBalanceQuery;
  }>;
  onCompleted?: () => void;
}

export function useResumeSubscription({
  refetchSubscriptionCreditBalance,
  onCompleted
}: UseResumeSubscriptionOptions) {
  const [resumeSubscription] = useMutation<
    ResumeSubscriptionMutation,
    ResumeSubscriptionMutationVariables
  >(resumeSubscriptionMutation);
  const [isResuming, setIsResuming] = useState(false);
  const [resumeSubscriptionError, setResumeSubscriptionError] =
    useState<Error | null>(null);

  const handleResume = useCallback(async () => {
    setResumeSubscriptionError(null);
    setIsResuming(true);
    try {
      await resumeSubscription();
      let lastData: SubscriptionCreditBalanceQuery | undefined;
      // max of 10 seconds delay before we give up
      for (let retries = 0; retries < 10; retries++) {
        const { data } = await refetchSubscriptionCreditBalance();
        lastData = data;
        if (
          !data.subscriptionCreditBalance?.pausedAt &&
          data.subscriptionCreditBalance?.cancelAtPeriodEnd === false &&
          // wait for webhook to process so we don't show "Action Required" briefly
          data.subscriptionCreditBalance.currentStatus !==
            CurrentSubscriptionStatus.PastDue
        ) {
          break;
        }
        await sleep(1000);
      }
      if (
        lastData?.subscriptionCreditBalance?.pausedAt ||
        lastData?.subscriptionCreditBalance?.cancelAtPeriodEnd
      ) {
        setResumeSubscriptionError(
          new Error(
            "We're experiencing technical difficulties updating your subscription status. Please refresh or contact support."
          )
        );
        return;
      }
      if (onCompleted) {
        onCompleted();
      }
    } catch (err: any) {
      setResumeSubscriptionError(err);
    } finally {
      setIsResuming(false);
    }
  }, [resumeSubscription, refetchSubscriptionCreditBalance, onCompleted]);

  return {
    handleResume,
    isResuming,
    resumeSubscriptionError
  };
}
